import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Snackbar,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EditNoteIcon from '@mui/icons-material/EditNote';
import StarRateIcon from '@mui/icons-material/StarRate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import { getMyDrinks, rateDrink, getSharedDrinkById } from '../../../../services/Clint/UserOrdersApi';
import { makeDrinkPublic } from '../../../../services/Clint/AiAgentApi';
import RenameDrinkDialog from './RenameDrinkDialog';

const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_BASE_URL || '';
const MyCreatedOrders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [myDrinks, setMyDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [renameDrinkDialogOpen, setRenameDrinkDialogOpen] = useState(false);
  const [selectedDrinkForRename, setSelectedDrinkForRename] = useState(null);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [selectedDrinkForRate, setSelectedDrinkForRate] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDrinkDetails, setSelectedDrinkDetails] = useState(null);
  const [rateValue, setRateValue] = useState(3);
  const [rateComment, setRateComment] = useState('');
  const [submittingRate, setSubmittingRate] = useState(false);
  const [publishingDrinkId, setPublishingDrinkId] = useState(null);
  const [sharingDrinkId, setSharingDrinkId] = useState(null);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchMyDrinks();
  }, []);

  const fetchMyDrinks = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getMyDrinks();
      setMyDrinks(response?.data?.order || []);
    } catch (err) {
      console.error('خطأ في جلب طلبات المشروبات:', err);
      setError(err.message || 'حدث خطأ في جلب طلبات المشروبات');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${MEDIA_BASE_URL}/${imagePath}`;
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'warning' },
      confirmed: { label: 'مؤكد', color: 'info' },
      in_preparation: { label: 'قيد التحضير', color: 'primary' },
      ready: { label: 'جاهز', color: 'success' },
      completed: { label: 'مكتمل', color: 'success' },
      cancelled: { label: 'ملغى', color: 'error' },
      0: { label: 'قيد المعالجة', color: 'warning' },
      1: { label: 'مكتمل', color: 'success' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const getApprovalLabel = (value) => (Number(value) === 1 ? 'تم الاعتماد' : 'غير معتمد');
  const getPublicLabel = (value) => (Number(value) === 1 ? 'عام' : 'خاص');

  const formatPrice = (value) => {
    const price = Number(value);
    if (!Number.isFinite(price)) return '-';
    return `${price.toFixed(2)} جنية`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleOpenRenameDrinkDialog = (drink) => {
    setSelectedDrinkForRename(drink);
    setRenameDrinkDialogOpen(true);
  };

  const handleCloseRenameDrinkDialog = () => {
    setRenameDrinkDialogOpen(false);
    setSelectedDrinkForRename(null);
  };

  const handleRenameDrinkSuccess = () => {
    fetchMyDrinks();
  };

  const handleOpenRateDialog = (drink) => {
    setSelectedDrinkForRate(drink);
    setRateValue(3);
    setRateComment('');
    setRateDialogOpen(true);
  };

  const handleOpenDetailsDialog = (drink) => {
    setSelectedDrinkDetails(drink);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDrinkDetails(null);
  };

  const handleCloseRateDialog = () => {
    if (submittingRate) return;
    setRateDialogOpen(false);
    setSelectedDrinkForRate(null);
    setRateValue(3);
    setRateComment('');
  };

  const handleSubmitRate = async () => {
    if (!selectedDrinkForRate?.id) return;
    if (!rateValue || rateValue < 1 || rateValue > 5) {
      setFeedback({
        open: true,
        message: 'الرجاء اختيار تقييم من 1 إلى 5',
        severity: 'warning',
      });
      return;
    }

    setSubmittingRate(true);
    try {
      await rateDrink({
        drink_id: selectedDrinkForRate.id,
        rate: rateValue,
        comment: rateComment,
      });

      setFeedback({
        open: true,
        message: 'تم إرسال تقييم المشروب بنجاح',
        severity: 'success',
      });
      handleCloseRateDialog();
      fetchMyDrinks();
    } catch (err) {
      setFeedback({
        open: true,
        message: err.message || 'حدث خطأ أثناء إرسال التقييم',
        severity: 'error',
      });
    } finally {
      setSubmittingRate(false);
    }
  };

  const handlePublishDrink = async (drink) => {
    if (!drink?.id || publishingDrinkId) return;

    setPublishingDrinkId(drink.id);
    try {
      const response = await makeDrinkPublic(drink.id);
      setFeedback({
        open: true,
        message: response?.message || 'تم عرض المشروب في منيو NUVVY بنجاح',
        severity: 'success',
      });
      fetchMyDrinks();
    } catch (err) {
      setFeedback({
        open: true,
        message: err.message || 'حدث خطأ أثناء عرض المشروب في المنيو',
        severity: 'error',
      });
    } finally {
      setPublishingDrinkId(null);
    }
  };

  const handleShareDrink = async (drink) => {
    const productCode = drink?.code;
    if (!productCode || sharingDrinkId) return;

    setSharingDrinkId(drink.id || productCode);
    try {
      // تحقق من أن كود المنتج صالح قبل مشاركته
      await getSharedDrinkById(productCode);

      const shareUrl = `${window.location.origin}/clint/shared-drink/${encodeURIComponent(productCode)}`;
      const shareTitle = drink?.drink_name || drink?.name || 'مشروب مخصص';
      const shareText = `مشروب مشترك: ${shareTitle}`;

      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }

      setFeedback({
        open: true,
        message: 'تم تجهيز رابط المشاركة بنجاح',
        severity: 'success',
      });
    } catch (err) {
      setFeedback({
        open: true,
        message: err.message || 'حدث خطأ أثناء مشاركة المشروب',
        severity: 'error',
      });
    } finally {
      setSharingDrinkId(null);
    }
  };

  const stats = useMemo(() => {
    const total = myDrinks.length;
    const completed = myDrinks.filter((d) => Number(d?.status) === 1 || d?.status === 'completed').length;
    const publicCount = myDrinks.filter((d) => Number(d?.is_public) === 1).length;
    const averagePrice =
      total > 0
        ? myDrinks.reduce((sum, d) => sum + (Number(d?.price) || 0), 0) / total
        : 0;

    return {
      total,
      completed,
      publicCount,
      averagePrice,
    };
  }, [myDrinks]);

  return (
    <Box dir="rtl" sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                مشروباتي المنشأة
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                لوحة منظمة لمتابعة المشروبات، النشر، التقييم، والمشاركة.
              </Typography>
            </Box>
          </Box>

          <Stack
            direction={{ xs: 'row', sm: 'row' }}
            spacing={1}
            sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}
          >
            <Chip color="primary" label={`الإجمالي: ${stats.total}`} sx={{ fontWeight: 700 }} />
            <Chip color="success" label={`مكتمل: ${stats.completed}`} sx={{ fontWeight: 700 }} />
            <Chip color="secondary" label={`عام: ${stats.publicCount}`} sx={{ fontWeight: 700 }} />
            <Chip variant="outlined" label={`متوسط السعر: ${formatPrice(stats.averagePrice)}`} sx={{ fontWeight: 700 }} />
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : myDrinks.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: alpha(theme.palette.grey[500], 0.08),
          }}
        >
          <RestaurantMenuIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد طلباتي التي أنشأتها
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لم تقم بطلب أي منتجات من القائمة حتى الآن
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.5, md: 2 }}>
          {myDrinks.map((drink, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`${drink?.id || 'drink'}-${index}`}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: isMobile ? 'none' : 'translateY(-6px)',
                    boxShadow: isMobile ? undefined : `0 16px 32px ${alpha(theme.palette.common.black, 0.14)}`,
                  },
                }}
              >
                {drink.image ? (
                  <Box
                    component="img"
                    src={getImageUrl(drink.image)}
                    alt={drink.name}
                    sx={{
                      width: '100%',
                      height: { xs: 170, sm: 180 },
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: 170, sm: 180 },
                      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.26)}, ${alpha(theme.palette.grey[700], 0.16)})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <RestaurantMenuIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  </Box>
                )}

                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, flex: 1, fontSize: { xs: '1rem', md: '1.05rem' } }}>
                      {drink.name || drink.drink_name}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {drink.description || 'بدون وصف'}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Typography variant="body2" color="text.secondary">
                      الحالة:
                    </Typography>
                    {getStatusChip(drink.status)}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(drink.created_at)}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatPrice(drink.price)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {drink.taste && <Chip size="small" label={`الطعم: ${drink.taste}`} variant="outlined" />}
                    {drink.temperature && <Chip size="small" label={`الحرارة: ${drink.temperature}`} variant="outlined" />}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <Chip size="small" color="info" label={getApprovalLabel(drink.is_approved)} />
                    <Chip size="small" color="secondary" label={getPublicLabel(drink.is_public)} />
                  </Box>

                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleOpenDetailsDialog(drink)}
                      sx={{ borderRadius: 2 }}
                    >
                      عرض كل البيانات
                    </Button>

                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={<EditNoteIcon />}
                          onClick={() => handleOpenRenameDrinkDialog(drink)}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                          إنشاء اسم خاص بي
                        </Button>
                      </Grid>

                      {Number(drink.is_public) === 0 && (
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handlePublishDrink(drink)}
                            disabled={publishingDrinkId === drink.id}
                            sx={{ borderRadius: 2, fontWeight: 700 }}
                          >
                            {publishingDrinkId === drink.id ? 'جاري النشر...' : 'عرض المشروب في منيو NUVVY'}
                          </Button>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<StarRateIcon />}
                          onClick={() => handleOpenRateDialog(drink)}
                          sx={{ borderRadius: 2 }}
                        >
                          تقييم
                        </Button>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="info"
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => handleShareDrink(drink)}
                          disabled={sharingDrinkId === drink.id}
                          sx={{ borderRadius: 2 }}
                        >
                          {sharingDrinkId === drink.id ? 'جاري التجهيز...' : 'مشاركة'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <RenameDrinkDialog
        open={renameDrinkDialogOpen}
        onClose={handleCloseRenameDrinkDialog}
        drink={selectedDrinkForRename}
        onSuccess={handleRenameDrinkSuccess}
      />

      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          تفاصيل المشروب: {selectedDrinkDetails?.name || selectedDrinkDetails?.drink_name || '-'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDrinkDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`الحالة: ${Number(selectedDrinkDetails.status) === 1 ? 'مكتمل' : 'قيد المعالجة'}`} />
                <Chip label={`الاعتماد: ${getApprovalLabel(selectedDrinkDetails.is_approved)}`} color="info" />
                <Chip label={`الظهور: ${getPublicLabel(selectedDrinkDetails.is_public)}`} color="secondary" />
                <Chip label={`السعر: ${formatPrice(selectedDrinkDetails.price)}`} color="success" />
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  البيانات الأساسية
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>drink_name:</strong> {selectedDrinkDetails.drink_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>name:</strong> {selectedDrinkDetails.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>taste:</strong> {selectedDrinkDetails.taste || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>temperature:</strong> {selectedDrinkDetails.temperature || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>created_at:</strong> {formatDate(selectedDrinkDetails.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>image:</strong> {selectedDrinkDetails.image || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  المواد ({selectedDrinkDetails.materials?.length || 0})
                </Typography>
                {!selectedDrinkDetails.materials?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد مواد</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDrinkDetails.materials.map((m, idx) => {
                      const unit = m?.material?.measurment_unit?.abbreviation || m?.material?.measurment_unit?.name || '-';
                      return (
                        <Box
                          key={`${m?.id || 'mat'}-${idx}`}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {m?.material?.name || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            الكمية: {m?.quantity || '-'} {unit}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  التقييمات ({selectedDrinkDetails.ratings?.length || 0})
                </Typography>
                {!selectedDrinkDetails.ratings?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد تقييمات</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDrinkDetails.ratings.map((r, idx) => (
                      <Box
                        key={`${r?.id || 'rating'}-${idx}`}
                        sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.100' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                          <Rating value={Number(r?.rate) || 0} readOnly size="small" />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(r?.created_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {r?.comment || 'بدون تعليق'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>

                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: '#111',
                    color: '#f3f3f3',
                    fontSize: 12,
                    overflow: 'auto',
                    direction: 'ltr',
                    textAlign: 'left',
                  }}
                >
                  {JSON.stringify(selectedDrinkDetails, null, 2)}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rateDialogOpen} onClose={handleCloseRateDialog} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          تقييم المشروب: {selectedDrinkForRate?.name || selectedDrinkForRate?.drink_name || '-'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                اختر التقييم
              </Typography>
              <Rating
                value={rateValue}
                onChange={(event, newValue) => setRateValue(newValue || 1)}
                max={5}
              />
            </Box>

            <TextField
              fullWidth
              label="تعليق"
              value={rateComment}
              onChange={(e) => setRateComment(e.target.value)}
              multiline
              rows={3}
              placeholder="اكتب رأيك في المشروب"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRateDialog} disabled={submittingRate}>
            إلغاء
          </Button>
          <Button variant="contained" onClick={handleSubmitRate} disabled={submittingRate}>
            {submittingRate ? 'جاري الإرسال...' : 'إرسال التقييم'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyCreatedOrders;
